import React, { useEffect, useRef, useState } from 'react';

import { Box, Button, Stack, Typography } from '../../../ui/core';
import { floatSurfaceSx, scrollY } from '../../../ui/styles';
import RideLiveMap from '../RideLiveMap';
import RideLiveState from '../RideLiveState';
import { PATH, stateAtStep } from './rideSimulator';
import { describeChange } from '../../../hooks/useRideLive';
import MapErrorBoundary from '../MapErrorBoundary';

// The cadence the location pipeline actually pings at.
const TICK_MS = 2500;
const FEED_LIMIT = 30;

/**
 * The live ride page, driven by a simulated stream.
 *
 * It exists so the map, the car's glide, the sprite frames and the panel can
 * be judged without a driver on the road, a rider with the app open, and a
 * backend between them. Everything below the state source is the real
 * component, so what it shows is what a ride shows.
 *
 * Behind the same login as every other page: it holds no real data, but an
 * unauthenticated page is a page anyone can open.
 */
const RideSimPage = () => {
  const [step, setStep] = useState(0);
  const [running, setRunning] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [state, setState] = useState(() => stateAtStep(0));
  const [feed, setFeed] = useState([]);
  const previous = useRef(null);

  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setStep((current) => (current + 1) % (PATH.length + 4)), TICK_MS / speed);
    return () => clearInterval(id);
  }, [running, speed]);

  // Mirrors what useRideLive does with each message, so the feed reads the same.
  useEffect(() => {
    const next = stateAtStep(step);
    const changes = describeChange(previous.current, next);
    previous.current = next;
    setState(next);
    setFeed((current) => [{ at: new Date().toISOString(), changes }, ...current].slice(0, FEED_LIMIT));
  }, [step]);

  return (
    <Box sx={{ position: 'relative', height: '100vh' }}>
      <MapErrorBoundary>
        <RideLiveMap state={state} />
      </MapErrorBoundary>

      <Stack
        spacing={2}
        sx={{
          position: 'absolute',
          top: 24,
          left: 24,
          bottom: 24,
          width: 420,
          p: 2,
          backgroundColor: (theme) => theme.palette.background.paper,
          ...floatSurfaceSx,
          ...scrollY,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Simulated ride</Typography>
          <Typography variant="body2" color="text.secondary">
            step {step} / {PATH.length}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={() => setRunning(!running)}>
            {running ? 'Pause' : 'Play'}
          </Button>
          <Button size="small" variant="outlined" onClick={() => setStep((current) => current + 1)}>
            Step
          </Button>
          {[1, 2, 4].map((option) => (
            <Button
              key={option}
              size="small"
              variant={speed === option ? 'contained' : 'outlined'}
              onClick={() => setSpeed(option)}
            >
              {option}x
            </Button>
          ))}
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              previous.current = null;
              setFeed([]);
              setStep(0);
            }}
          >
            Reset
          </Button>
        </Stack>

        <RideLiveState state={state} feed={feed} connected={running} />
      </Stack>
    </Box>
  );
};

export default RideSimPage;
